/**
 * System-field entries for the CRM opportunity tools.
 *
 * Default system fields shared by providers that do not declare a narrower writable subset.
 *
 * `CrmAdapter.opportunitySystemFieldSchema` can override this list. The selected subset is stored
 * in the agent's CRM schema snapshot so UI validation and runtime registration use the same source
 * of truth.
 *
 * ## Why this file exists
 *
 * `adapter.getOpportunityFieldsSchema()` (across every provider —
 * Kommo, GHL, Pipedrive) returns **only** custom fields. System
 * attributes (`name`, `price`, `pipeline_id`, `status_id`) are
 * top-level opportunity properties on every provider's model and don't
 * appear in the custom-field discovery response. Without this constant:
 *
 *   - A whitelist of `['name', 'price', 'lead_source']` would lose
 *     `name` and `price` at schema-build time because they're not in
 *     `ctx.crm.fieldSchema`.
 *   - A system-only whitelist (`['name', 'price']`) would make the
 *     update-fields-tool creator return `null` and the tool would
 *     vanish from the LLM with no signal to the operator.
 *
 * The constant remains the legacy-snapshot fallback and the default for existing providers.
 *
 * ## Scope
 *
 * Only `name` and `price` are listed today — these are the two
 * system attributes the service-layer dispatcher
 * (`service-agent-tools.ts → dispatchCodeValues`) routes to typed
 * `UpdateOpportunityParams` slots. `pipeline_id` / `status_id` are
 * deliberately NOT here because stage/pipeline moves go through the
 * separate `crm_move_opportunity_stage` tool (two-tool invariant).
 */

import type { CrmFieldSchema } from '@/lib/crm/types';

export const SYSTEM_FIELD_SCHEMA: readonly CrmFieldSchema[] = [
  {
    id: 'name',
    code: 'name',
    label: 'Name',
    type: 'text',
    system: true,
  },
  {
    id: 'price',
    code: 'price',
    label: 'Price',
    type: 'number',
    system: true,
  },
];

/**
 * Fast-lookup set of system field codes. Exported alongside the full
 * schema so consumers that only need code presence (e.g. the prefetch
 * drift filter) don't have to rebuild the set on every call.
 */
export const SYSTEM_FIELD_CODES: ReadonlySet<string> = new Set(
  SYSTEM_FIELD_SCHEMA.map((f) => f.code)
);

/**
 * Combine a provider's custom schema with the system fields it can actually write.
 *
 * The generic default remains name + price. A provider may pass a narrower set (for example,
 * SalesDrive supports changing its application comment/name but has no direct total-price write).
 */
export function mergeWritableFieldSchema(
  customFields: CrmFieldSchema[],
  systemFields: readonly CrmFieldSchema[] = SYSTEM_FIELD_SCHEMA
): CrmFieldSchema[] {
  const byCode = new Map<string, CrmFieldSchema>();
  for (const field of systemFields) {
    byCode.set(field.code, { ...field, system: true });
  }
  for (const field of customFields) {
    if (!byCode.has(field.code)) {
      byCode.set(field.code, field);
    }
  }
  return [...byCode.values()];
}
